import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';

export const useWebSocket = (conversationId, user) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const socketRef = useRef(null);

  useEffect(() => {
    // Create socket connection
    const newSocket = io(SOCKET_URL, {
      path: '/api/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    // Connection events
    newSocket.on('connect', () => {
      console.log('🔌 WebSocket connected:', newSocket.id);
      setIsConnected(true);
    });

    newSocket.on('disconnect', () => {
      console.log('🔌 WebSocket disconnected');
      setIsConnected(false);
    });

    newSocket.on('error', (error) => {
      console.error('❌ WebSocket error:', error);
    });

    // Cleanup on unmount
    return () => {
      newSocket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!socket || !conversationId || !user) return;

    // Join the conversation room
    socket.emit('join_conversation', {
      conversation_id: conversationId,
      user: {
        id: user.id,
        name: user.name,
        avatar: user.avatar || user.profile_picture_url
      }
    });

    console.log('📩 Joined conversation:', conversationId);

    // Listen for successful join
    socket.on('joined_conversation', (data) => {
      console.log('✅ Successfully joined conversation:', data);
    });

    // Listen for message history
    socket.on('message_history', (data) => {
      console.log('📜 Message history received:', data.messages.length, 'messages');
      setMessages(data.messages);
    });

    // Listen for new messages
    socket.on('new_message', (message) => {
      console.log('💬 New message received:', message);
      setMessages(prev => [...prev, message]);
    });

    // Listen for typing indicators
    socket.on('user_typing', (data) => {
      console.log('⌨️ User typing:', data);
      if (data.is_typing) {
        setTypingUsers(prev => [...new Set([...prev, data.user_name])]);
      } else {
        setTypingUsers(prev => prev.filter(name => name !== data.user_name));
      }
    });

    // Cleanup listeners when conversation changes
    return () => {
      socket.off('joined_conversation');
      socket.off('message_history');
      socket.off('new_message');
      socket.off('user_typing');
    };
  }, [socket, conversationId, user]);

  // Send message function
  const sendMessage = (content) => {
    if (!socket || !isConnected || !conversationId || !user) {
      console.warn('⚠️ Cannot send message - not connected');
      return;
    }

    socket.emit('send_message', {
      conversation_id: conversationId,
      content,
      sender: {
        id: user.id,
        name: user.name,
        avatar: user.avatar || user.profile_picture_url
      }
    });

    console.log('📤 Message sent:', content);
  };

  // Send typing indicator
  const sendTypingIndicator = (isTyping) => {
    if (!socket || !isConnected || !conversationId || !user) return;

    socket.emit('typing', {
      conversation_id: conversationId,
      user_name: user.name,
      is_typing: isTyping
    });
  };

  return {
    socket,
    isConnected,
    messages,
    typingUsers,
    sendMessage,
    sendTypingIndicator,
    setMessages  // Allow manual message updates
  };
};
